import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../card';
import { Button } from '../../button/button';
import { CopyLinkButton } from '../../copy-link-button/copy-link-button';
import { Checkbox } from '../../checkbox';
import {
  BarChart2,
  Download,
  Trash2,
  CircleX,
  CheckCircle,
  Circle,
} from 'lucide-react';
import { format } from 'date-fns';
import { DeleteAllConversationsButton } from '../components/delete-all-conversations-button/delete-all-conversations-button';
import conversationSimulationData from '../../../../data/conversationSimulation/data.json';
import { PastSimulationsProps } from './types';
import { useSimulations } from '../../../../contexts/SimulationContext';
import { useSimulationStore } from '../../../../stores/simulationStore';
import { conversationSimulationExporter } from '../../../../lib/utils/conversation-simulation/conversation-simulation-exporter';
import { useTutorialStore } from '../../../../stores/tutorialStore';

export function PastSimulations({
  setSelectedAgent,
  isSimulating,
  onSimulationSelect,
}: PastSimulationsProps): JSX.Element {
  const { simulations, deleteSimulation } = useSimulations();
  const navigate = useNavigate();
  const { markMilestoneComplete } = useTutorialStore();

  const {
    resetSimulation,
    selectedSimulation,
    selectedSimulations,
    setSimulationState,
  } = useSimulationStore();
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);

  const handleDeleteSimulation = async (
    simulationId: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this simulation?')) {
      try {
        await deleteSimulation(simulationId);
        setSimulationState({
          selectedSimulations: selectedSimulations.filter(
            (id) => id !== simulationId,
          ),
        });
        if (selectedSimulation === simulationId) {
          setSelectedAgent(null);
          setSimulationState({
            selectedSimulation: null,
            selectedPersonaTab: null,
            personaSimulations: {},
            personaMessages: {},
            selectedPersonas: [],
            isSimulating: false,
            simulationProgress: null,
          });
        }
      } catch (error) {
        console.error('Error deleting simulation:', error);
        toast.error('Failed to delete simulation');
      }
    }
  };

  const handleExportSelected = async () => {
    const toExport = simulations.filter((s) =>
      selectedSimulations.includes(s.id),
    );
    if (toExport.length === 0) return;
    if (toExport.length === 1) {
      conversationSimulationExporter.exportOne(toExport[0]);
    } else {
      await conversationSimulationExporter.exportMany(toExport);
    }
    setSimulationState({ selectedSimulations: [] });
  };

  const handleSimulationClick = (simulationId: string) => {
    const simulation = simulations.find((s) => s.id === simulationId);
    if (selectedSimulation === simulationId) {
      setSelectedAgent(null);
      resetSimulation();
      onSimulationSelect?.(null);
    } else if (simulation) {
      setSimulationState({
        selectedSimulation: simulationId,
        selectedPersonaTab: simulation.persona_id,
        personaSimulations: {
          [simulation.persona_id]: simulationId,
        },
        personaMessages: {},
        selectedPersonas: [],
        isSimulating: false,
        simulationProgress: null,
      });
      onSimulationSelect?.(simulationId);
    }
  };

  const handleEvaluateSimulation = (
    simulationId: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    markMilestoneComplete('evaluationOpened');
    navigate(`/evaluations?simulationId=${simulationId}`);
  };

  return (
    <Card
      id="past-simulations"
      className="rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a]"
    >
      <CardHeader className="pb-2">
        <div className="flex justify-between">
          <CardTitle className="font-prompt-safe-com-semantic-heading-5 text-prompt-safecomvulcan">
            Past simulations
          </CardTitle>
          {!isSimulating && simulations?.length > 0 && (
            <DeleteAllConversationsButton
              setSelectedAgent={setSelectedAgent}
              simulationsLength={simulations?.length}
            />
          )}
        </div>
        {!isSimulating && simulations?.length > 0 && (
          <div className="flex items-center justify-between gap-3 pt-3">
            {selectedSimulations.length > 0 ? (
              <>
                <Button
                  variant="link"
                  className="p-0 text-xs text-prompt-safecomoutrageous-orange"
                  onClick={() =>
                    setSimulationState({ selectedSimulations: [] })
                  }
                  tooltipContent={
                    conversationSimulationData?.deselectAllButton?.tooltip
                  }
                >
                  <Circle />
                  Deselect all ({selectedSimulations.length})
                </Button>
                <Button
                  variant="link"
                  className="p-0 text-xs text-prompt-safecomoutrageous-orange"
                  onClick={handleExportSelected}
                  tooltipContent={
                    conversationSimulationData?.exportSelectedButton?.tooltip
                  }
                >
                  <Download />
                  Export selected ({selectedSimulations.length})
                </Button>
              </>
            ) : (
              <Button
                variant="link"
                className="p-0 text-xs text-prompt-safecomoutrageous-orange"
                onClick={() =>
                  setSimulationState({
                    selectedSimulations: simulations.map((s) => s.id),
                  })
                }
                tooltipContent={
                  conversationSimulationData?.selectAllButton?.tooltip
                }
              >
                <CheckCircle />
                Select all
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="max-h-[calc(100vh-500px)] overflow-y-auto pr-2 pt-2">
          <div className="space-y-2">
            {simulations.map((simulation) => {
              const showCheckbox =
                hoveredCardId === simulation.id ||
                selectedSimulations.length > 0;
              return (
                <div
                  key={simulation.id}
                  className={`relative cursor-pointer rounded-lg border p-3 ${
                    selectedSimulation === simulation.id
                      ? 'bg-prompt-safecomoutrageous-orange/10 border-2 border-prompt-safecomoutrageous-orange'
                      : 'border-[#00000020] bg-white hover:border-prompt-safecomoutrageous-orange'
                  }`}
                  onClick={() => handleSimulationClick(simulation.id)}
                  onMouseEnter={() => setHoveredCardId(simulation.id)}
                  onMouseLeave={() => setHoveredCardId(null)}
                >
                  <div
                    className={`absolute left-3 top-3 transition-opacity duration-150 ${
                      showCheckbox
                        ? 'opacity-100'
                        : 'pointer-events-none opacity-0'
                    }`}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selectedSimulations.includes(simulation.id)}
                      onCheckedChange={(checked) => {
                        setSimulationState({
                          selectedSimulations:
                            checked === true
                              ? [...selectedSimulations, simulation.id]
                              : selectedSimulations.filter(
                                  (id) => id !== simulation.id,
                                ),
                        });
                      }}
                      className="flex h-4 min-h-4 w-4 min-w-4 shrink-0 items-center justify-center rounded-full border border-prompt-safecomoutrageous-orange shadow-none data-[state=checked]:bg-prompt-safecomoutrageous-orange data-[state=checked]:text-white [&_svg]:h-3 [&_svg]:w-3"
                    />
                  </div>
                  {selectedSimulation === simulation.id && (
                    <div className="absolute -right-2 -top-2">
                      <CircleX
                        className="fill-prompt-safecomfantasy text-prompt-safecomoutrageous-orange"
                        size={20}
                      />
                    </div>
                  )}
                  <h3
                    className={`font-medium text-prompt-safecomvulcan transition-[padding] duration-150 ${
                      showCheckbox ? 'pl-7' : 'pl-0'
                    }`}
                  >
                    {simulation.name}
                  </h3>
                  <p className="text-sm text-prompt-safecomabbey">
                    {format(new Date(simulation.created_at), 'PPpp')}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-prompt-safecomabbey">
                      {simulation.messages.length} messages
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:text-prompt-safecomoutrageous-orange"
                      onClick={(e: React.MouseEvent) =>
                        conversationSimulationExporter.exportOne(simulation, e)
                      }
                      tooltipContent={
                        conversationSimulationData?.downloadConversationButton
                          ?.tooltip
                      }
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:text-red-600"
                      onClick={(e: React.MouseEvent) =>
                        handleDeleteSimulation(simulation.id, e)
                      }
                      tooltipContent={
                        conversationSimulationData?.deleteConversationButton
                          ?.tooltip
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:text-prompt-safecomoutrageous-orange"
                      onClick={(e: React.MouseEvent) =>
                        handleEvaluateSimulation(simulation.id, e)
                      }
                      tooltipContent="Evaluate this conversation"
                    >
                      <BarChart2 className="h-4 w-4" />
                    </Button>
                    <CopyLinkButton
                      path={`/conversation-simulation/${simulation.id}`}
                      tooltipText={
                        conversationSimulationData?.linkSimulationButton
                          ?.tooltip
                      }
                      className="h-6 w-6 p-0 text-prompt-safecomvulcan hover:text-prompt-safecomoutrageous-orange"
                    />
                  </div>
                </div>
              );
            })}

            {simulations.length === 0 && (
              <div className="py-4 text-center text-prompt-safecomabbey">
                No simulations yet
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
